import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Box, Container, Paper, Tabs, Tab, Button, ToggleButton, ToggleButtonGroup, TextField } from '@mui/material';
import { uk } from 'date-fns/locale';
import { useAuth } from '../auth/Auth';
import "../historyd/History.css";

const styles = {
  dateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px'
  },
  dateInput: {
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px'
  },
  fopContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    backgroundColor: '#f5f5f5',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  fopHeader: {
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '8px',
    marginBottom: '8px'
  },
  fopInfo: {
    marginTop: '8px'
  },
  fopMetadata: {
    color: '#666',
    fontSize: '0.9em',
    marginTop: '4px'
  },
  searchContainer: {
    marginBottom: '20px',
    width: '100%'
  }
};

const UserProfile = () => {
  const [User, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractorRestaurants, setContractorRestaurants] = useState([]);
  const [packageHistory, setPackageHistory] = useState([]);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [packageStartDate, setPackageStartDate] = useState('');
  const [packageEndDate, setPackageEndDate] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [fopStatus, setFopStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const [translations, setTranslations] = useState(null);
  const [language, setLanguage] = useState('uk-UA');

  const formatPackageName = (packageType) => {
    switch(packageType) {
      case 'START_CHAIN':
        return 'START Chain';
      case 'PRO_CHAIN':
        return 'PRO Chain';
      case 'EXCLUSIVE_CHAIN':
        return 'EXCLUSIVE Chain';
      default:
        return packageType;
    }
  };

  useEffect(() => {
    fetchUserData();
    if (user?.user_id) {
      fetchPackageHistory();
      fetchConversionHistory();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      const userId = user?.user_id;
      if (!userId) {
        setGeneralError(language === 'pl-PL' ? 'Użytkownik nie jest autoryzowany' : 'Користувач не авторизований');
        setLoading(false);
        return;
      }
  
      const regionResponse = await axios.get(`https://bank2.daolog.net/user-region/${userId}`);
      const userRegion = regionResponse.data.region;
      setLanguage(userRegion);

      const translationsResponse = await axios.get(`https://bank2.daolog.net/translations/historyd/${userRegion}`);
      setTranslations(translationsResponse.data);

      const response = await axios.get(`https://bank2.daolog.net/user-profile-kkk/${userId}`);
      setUser(response.data);

      if (response.data.contractor) {
        const restaurantsResponse = await axios.get(
          `https://bank2.daolog.net/contractor-restaurants-with-fops-kkk/${response.data.contractor.id}`
        );
        setContractorRestaurants(restaurantsResponse.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Помилка при отриманні даних користувача');
      setLoading(false);
    }
  };

  const fetchPackageHistory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `https://bank2.daolog.net/package-history-kkk/${user.user_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setPackageHistory(response.data);
    } catch (error) {
      console.error('Помилка при отриманні історії пакетів:', error);
      setError('Не вдалося завантажити історію пакетів');
    }
  };

  const fetchConversionHistory = async () => {
    try {
      const response = await axios.get(`https://bank2.daolog.net/conversion-history-kkk/${user.user_id}`);
      setConversionHistory(response.data);
    } catch (error) {
      console.error('Помилка при отриманні історії конвертацій:', error);
    }
  };

  const handleDownload = async (filePath) => {
    try {
      if (!filePath) {
        console.error('Шлях до файлу відсутній');
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('Токен авторизації відсутній');
        window.location.href = '/login';
        return;
      }

      const fileName = filePath.split(/[\\\/]/).pop();
      console.log('Attempting to download file:', fileName);

      const response = await axios({
        method: 'GET',
        url: `https://bank2.daolog.net/download-kkk/${user.user_id}/${encodeURIComponent(fileName)}`,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Download response received');

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('Download error details:', error.response || error);

      if (error.response?.status === 404) {
        alert('Файл не знайдено');
      } else if (error.response?.status === 401) {
        alert('Помилка авторизації при завантаженні файлу');
        window.location.href = '/login';
      } else {
        alert('Помилка при завантаженні файлу');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Дата відсутня';
    const date = new Date(dateString);
    const time = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    return `${date.toLocaleDateString('uk-UA')} ${time}`;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRestaurantChange = (event, newValue) => {
    setSelectedRestaurant(newValue);
  };

  const handleFopStatusChange = (event, newValue) => {
    if (newValue !== null) {
      setFopStatus(newValue);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const filteredConversionHistory = conversionHistory.filter(item => {
    if (!startDate && !endDate) return true;
    const itemDate = new Date(item.conversion_date);
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;
    
    if (startDateObj && !endDateObj) return itemDate >= startDateObj;
    if (!startDateObj && endDateObj) return itemDate <= endDateObj;
    return itemDate >= startDateObj && itemDate <= endDateObj;
  });

  const filteredPackageHistory = packageHistory.filter(item => {
    if (!packageStartDate && !packageEndDate) return true;
    const itemDate = new Date(item.last_package_change);
    const startDateObj = packageStartDate ? new Date(packageStartDate) : null;
    const endDateObj = packageEndDate ? new Date(packageEndDate) : null;
    
    if (startDateObj && !endDateObj) return itemDate >= startDateObj;
    if (!startDateObj && endDateObj) return itemDate <= endDateObj;
    return itemDate >= startDateObj && itemDate <= endDateObj;
  });

  const filterFopsByStatus = (fops) => {
    let filtered = fops;
    
    // Filter by status
    switch (fopStatus) {
      case 'active':
        filtered = filtered.filter(fop => !fop.del_date);
        break;
      case 'inactive':
        filtered = filtered.filter(fop => fop.del_date);
        break;
      default:
        break;
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(fop => 
        fop.name_fop.toLowerCase().includes(searchQuery) ||
        fop.created_username?.toLowerCase().includes(searchQuery) ||
        fop.deactivated_username?.toLowerCase().includes(searchQuery)
      );
    }

    return filtered;
  };

  const filteredRestaurants = selectedRestaurant === 'all'
    ? contractorRestaurants
    : contractorRestaurants.filter(restaurant => restaurant.id === selectedRestaurant);

  if (loading) return <Typography>Завантаження...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!User) return <Typography>Користувача не знайдено</Typography>;

  return (
    <Container maxWidth="md" style={{ marginTop: "120px" }}>
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        '& .MuiTabs-root': {
          display: 'flex',
          justifyContent: 'center'
        }
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
        >
          <Tab style={{ color: '#333', borderBottom: '2px solid #333', paddingBottom: '10px' }} label={translations.historyContr} />
          <Tab style={{ color: '#333', borderBottom: '2px solid #333', paddingBottom: '10px' }} label={translations.historyPacT} />
          <Tab style={{ color: '#333', borderBottom: '2px solid #333', paddingBottom: '10px' }} label={translations.historyConv} />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box sx={{
          padding: 3,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          marginBottom: 3
        }}>
          <Typography variant="h6" gutterBottom >{translations.historyContr}</Typography>

          <TextField
            fullWidth
            label={translations.search}
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            style={styles.searchContainer}
          />

          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={selectedRestaurant}
              exclusive
              onChange={handleRestaurantChange}
              aria-label="restaurant filter"
              sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <ToggleButton value="all">
              {translations.allRest}
              </ToggleButton>
              {contractorRestaurants.map((restaurant) => (
                <ToggleButton key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={fopStatus}
              exclusive
              onChange={handleFopStatusChange}
              aria-label="fop status"
              sx={{ justifyContent: 'center' }}
            >
              <ToggleButton value="all">
              {translations.allRest}
              </ToggleButton>
              <ToggleButton value="active">
              {translations.actv}
              </ToggleButton>
              <ToggleButton value="inactive">
              {translations.deactv}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ marginTop: 2 }}>
            {filteredRestaurants.map((restaurant) => {
              const filteredFops = filterFopsByStatus(restaurant.fops);
              if (filteredFops.length === 0) return null;

              return (
                <Box key={restaurant.id} sx={{ marginBottom: 3 }}>
                  <Typography variant="h6" sx={{ backgroundColor: 'primary.light', p: 1, color: 'white', borderRadius: 1 }}>
                    {restaurant.name}
                  </Typography>
                  <Box sx={{ marginLeft: 2, marginTop: 1 }}>
                    {filteredFops.map((fop) => (
                      <Box key={fop.id} style={styles.fopContainer}>
                        <div style={styles.fopHeader}>
                          <Typography variant="h6">
                            {fop.name_fop}
                          </Typography>
                        </div>
                        <div style={styles.fopInfo}>
                          <Typography>
                          {translations.ederpou}: {fop.edrpou}
                          </Typography>
                          <Typography color="green">
                          {translations.dateActivate}: {new Date(fop.created_at).toLocaleDateString()}
                          </Typography>
                          {fop.del_date && (
                            <Typography color="error">
                          {translations.dateDeactivate}: {new Date(fop.del_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </div>
                        <div style={styles.fopMetadata}>
                          {fop.created_username && (
                            <Typography>
                              {translations.create}: {fop.created_username}
                            </Typography>
                          )}
                          {fop.del_date && fop.deactivated_username && (
                            <Typography>
                              {translations.deact}: {fop.deactivated_username}
                            </Typography>
                          )}
                        </div>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

{activeTab === 1 && (
  <Box sx={{
    padding: 3,
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: 1
  }}>
    <Typography variant="h6" gutterBottom >{translations.historyPac}</Typography>
    
    <div style={styles.dateContainer}>
      <input
        type="date"
        value={packageStartDate}
        onChange={(e) => setPackageStartDate(e.target.value)}
        style={styles.dateInput}
        placeholder={translations.dateFrom}
      />
      <span>{translations.to}</span>
      <input
        type="date"
        value={packageEndDate}
        onChange={(e) => setPackageEndDate(e.target.value)}
        style={styles.dateInput}
        placeholder={translations.dateTo}
      />
    </div>

    <Box sx={{ marginTop: 2 }}>
      {filteredPackageHistory.sort((a, b) => new Date(b.last_package_change) - new Date(a.last_package_change))
        .map((item, index) => (
          <Paper key={`package-${index}`} style={{ padding: '15px', marginBottom: '10px' }}>
            <Typography>
            {translations.date}: {formatDate(item.last_package_change)}
            </Typography>
            <Typography>
            {translations.contr}: {item.contractor_name}
            </Typography>
            <Typography>
            {translations.userChange}: {item.username}
            </Typography>
            <Typography>
            {translations.oldPac}: {item.old_pac ? formatPackageName(item.old_pac) : 'Раніше пакета не було обрано'}
            </Typography>
            <Typography>
            {translations.newPac}: {formatPackageName(item.new_pac)}
            </Typography>
          </Paper>
        ))}
    </Box>
  </Box>
)}
      {activeTab === 2 && (
        <Box sx={{
          padding: 3,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Typography variant="h6" gutterBottom>{translations.historyConv}</Typography>

          <div style={styles.dateContainer}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.dateInput}
              placeholder="Дата від..."
            />
            <span>{translations.to}</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.dateInput}
              placeholder="Дата до..."
            />
          </div>

          <Box sx={{ marginTop: 2 }}>
            {filteredConversionHistory.sort((a, b) => new Date(b.conversion_date) - new Date(a.conversion_date))
              .map((item, index) => (
                <Paper key={`conversion-${index}`} style={{ padding: '15px', marginBottom: '10px' }}>
                  <Typography variant="subtitle1" gutterBottom>
                  {translations.stateNumber} #{item.id}
                  </Typography>
                  <Typography>
                  {translations.dateConv}: {formatDate(item.conversion_date)}
                  </Typography>
                  <Typography>
                  {translations.type}: {item.bank_type}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <button
                      className='download-button'
                      variant="contained"
                      size="small"
                      onClick={() => handleDownload(item.path)}
                    >
                      {translations.download}
                    </button>
                  </Box>
                </Paper>
              ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default UserProfile;